<template>
  <div ref="text-line"
    class="expandable-text-line"
    :style="style" :role="useClick ? 'button' : null"
    :tabindex="useClick ? 0 : null"
    :aria-expanded="useClick ? expanded : null"
    :aria-label="useClick ? (expanded ? 'Collapse text' : 'Expand text') : null"
    v-on="shouldHover ? { mouseenter: expand, mouseleave: collapse } : { click }" @keydown.enter="onKeydownActivate"
    @keydown.space.prevent="onKeydownActivate">
    <slot />
  </div>
</template>

<script>
export default {
  name: 'ExpandableTextLine',

  props: {
    useClick: Boolean,
    forceExpand: Boolean,
    duration: { type: Number, default: 0.2 },
    collapseTextOnLeaveRatio: { type: Number, default: 1 }
  },

  data () {
    return {
      expandedClass: '__extl-expanded',
      timeoutOnCollapse: null,
      timeoutOnExpand: null,
      touch: false,
      initialH: null,
      initialW: null,
      expanded: false
    }
  },

  computed: {
    shouldHover () {
      return !this.useClick && !this.touch
    },
    style () {
      return {
        transition: `height ${this.duration}s ease-in-out`
      }
    }
  },

  mounted () {
    this.manageWidth()
    this.addClickOrTouchListener()
  },

  beforeDestroy () {
    if (this._onFirstTouch) document.removeEventListener('touchstart', this._onFirstTouch)
    if (this._onFirstClick) document.removeEventListener('click', this._onFirstClick)
    if (this._onTouchOutside) document.removeEventListener('touchstart', this._onTouchOutside)
    if (this._onClickOutside) document.removeEventListener('click', this._onClickOutside)
    if (this._onWindowResize) window.removeEventListener('resize', this._onWindowResize)
    if (this._ro) this._ro.disconnect()
    if (this._resizeTimeout) clearTimeout(this._resizeTimeout)
    if (this.timeoutOnCollapse) clearTimeout(this.timeoutOnCollapse)
    if (this.timeoutOnExpand) clearTimeout(this.timeoutOnExpand)
  },

  methods: {
    click () {
      const el = this.$refs['text-line']
      if (!el.classList.contains(this.expandedClass)) {
        this.expand()
      } else {
        this.collapse()
      }
    },

    onKeydownActivate () {
      if (this.useClick) {
        this.click()
      }
    },

    expand () {
      /** @type HTMLElement */
      const el = this.$refs['text-line']
      if (!this.needsExpand(el) && !this.forceExpand) { return }
      if (this.forceExpand && el.classList.contains(this.expandedClass)) { return }
      const dimensions = this.dimensionsUsingClone(el)
      clearTimeout(this.timeoutOnCollapse)
      // Set explicit starting height so the transition has a baseline (px -> px)
      el.style.height = dimensions.collapseHeight
      // Force reflow to register the starting height
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight
      // Apply the target height in the next frame to trigger transition
      requestAnimationFrame(() => {
        el.style.willChange = 'height'
        el.classList.add(this.expandedClass)
        // keep overflow hidden during animation to avoid scrollbar snap
        el.style.overflow = 'hidden'
        el.style.height = dimensions.expandHeight
        this.expanded = true
        this.$emit('start-expand')
        this.timeoutOnExpand = setTimeout(() => {
          el.style.height = 'auto'
          el.style.whiteSpace = 'unset'
          el.style.overflow = 'auto'
          // settle and remove will-change
          requestAnimationFrame(() => { el.style.willChange = null })
        }, this.duration * 1000)
      })
    },

    collapse () {
      if (!this.expanded) { return }
      if (this.forceExpand) { return }
      const el = this.$refs['text-line']
      const dimensions = this.dimensionsUsingClone(el)
      clearTimeout(this.timeoutOnExpand)
      setTimeout(() => {
        el.style.willChange = 'height'
        // hide overflow during collapse animation
        el.style.overflow = 'hidden'
        el.style.height = dimensions.collapseHeight
        el.scroll({ top: 0, behavior: 'smooth' })
        this.$emit('start-collapse')
        this.timeoutOnCollapse = setTimeout(() => {
          el.style.whiteSpace = null
          el.classList.remove(this.expandedClass)
          el.scrollTop = 0
          el.style.height = null
          // base CSS sets overflow hidden; clear inline value
          el.style.overflow = null
          this.expanded = false
          el.style.willChange = null
        }, this.duration * 1000 * this.collapseTextOnLeaveRatio)
      })
    },

    needsExpand (el) {
      return el.offsetWidth < el.scrollWidth
    },

    /**
     * @param {HTMLElement} lineEl
     * @param {boolean} resetH
     * @return {{collapseWidth:string, expandWidth:string, collapseHeight:string, expandHeight:string}}
     */
    dimensionsForExpand (lineEl, resetH) {
      const { height, width } = getComputedStyle(lineEl)
      const collapseHeight = height
      const collapseWidth = width
      const preH = lineEl.style.height

      lineEl.style.height = 'auto'
      lineEl.style.whiteSpace = 'initial'
      lineEl.classList.add(this.expandedClass)

      const expandRect = getComputedStyle(lineEl)
      const expandHeight = expandRect.height
      const expandWidth = expandRect.width

      lineEl.style.whiteSpace = null
      lineEl.style.height = resetH ? preH : collapseHeight
      lineEl.classList.remove(this.expandedClass)
      // Force repaint to make sure the animation is triggered correctly.
      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(lineEl).height

      return { collapseWidth, expandWidth, collapseHeight, expandHeight }
    },

    /**
     * @param {HTMLElement} lineEl
     * @return {{collapseWidth:string, expandWidth:string, collapseHeight:string, expandHeight:string}}
     */
    dimensionsForCollapse (lineEl) {
      const { height, width, whiteSpace } = getComputedStyle(lineEl)
      const expandHeight = height
      const expandWidth = width

      lineEl.style.height = null
      lineEl.style.whiteSpace = 'nowrap'
      lineEl.classList.remove(this.expandedClass)

      const collapseRect = getComputedStyle(lineEl)
      const collapseHeight = collapseRect.height
      const collapseWidth = collapseRect.width

      lineEl.style.whiteSpace = whiteSpace
      lineEl.style.height = expandHeight
      lineEl.classList.add(this.expandedClass)

      // eslint-disable-next-line no-unused-expressions
      getComputedStyle(lineEl).height

      return { collapseWidth, expandWidth, collapseHeight, expandHeight }
    },

    dimensionsUsingClone (lineEl) {
      const computed = getComputedStyle(lineEl)
      const width = computed.width
      const parent = lineEl.parentElement || document.body
      // Create clone off-DOM
      const clone = lineEl.cloneNode(true)
      clone.style.position = 'absolute'
      clone.style.left = '-99999px'
      clone.style.top = '0'
      clone.style.visibility = 'hidden'
      clone.style.height = 'auto'
      clone.style.width = width
      clone.style.overflow = 'visible'
      parent.appendChild(clone)

      // Collapsed measurement (single line)
      clone.classList.remove(this.expandedClass)
      clone.style.whiteSpace = 'nowrap'
      const collapsedRect = getComputedStyle(clone)
      const collapseHeight = collapsedRect.height
      const collapseWidth = collapsedRect.width

      // Expanded measurement (wrapped)
      clone.classList.add(this.expandedClass)
      clone.style.whiteSpace = 'initial'
      const expandedRect = getComputedStyle(clone)
      const expandHeight = expandedRect.height
      const expandWidth = expandedRect.width

      parent.removeChild(clone)

      return { collapseWidth, expandWidth, collapseHeight, expandHeight }
    },

    manageWidth () {
      const el = this.$refs['text-line']
      if (!el) return
      const dimensions = this.dimensionsForExpand(el, true)
      if (dimensions.collapseWidth !== dimensions.expandWidth) {
        // this could be a case when the line's parent doesn't have an explicit width
        // make sure that the line will be the same width in both states (expanded & collapsed)
        console.warn('ExpandableTextLine: The parent width does not seem to be set explicitly. Therefore, the maximum width of the collapsed line must be set on the fly to match the natural dimensions in the expanded state. This can potentially affect performance. Please consider explicitly setting the width of the parent element or the width of the line itself.')
        el.style.width = dimensions.expandWidth

        if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
          this._ro = new ResizeObserver(() => {
            this.setMaxWidth(el)
          })
          const target = el.parentElement || el
          this._ro.observe(target)
        } else {
          this._onWindowResize = () => {
            clearTimeout(this._resizeTimeout)
            this._resizeTimeout = setTimeout(() => {
              requestAnimationFrame(() => { this.setMaxWidth(el) })
            }, 100)
          }
          window.addEventListener('resize', this._onWindowResize)
        }
      }
    },

    setMaxWidth (el) {
      const preW = el.style.width
      el.style.width = null
      if (el.classList.contains(this.expandedClass)) {
        el.style.width = getComputedStyle(el).width
      } else {
        const dim = this.dimensionsForExpand(el)
        if (dim.collapseWidth !== dim.expandWidth) {
          el.style.width = dim.expandWidth
        } else {
          el.style.width = preW
        }
      }
    },

    addClickOrTouchListener () {
      this._onFirstTouch = (e) => this.onFirstTouch(e)
      this._onFirstClick = (e) => this.onFirstClick(e)
      document.addEventListener('touchstart', this._onFirstTouch, { passive: true })
      document.addEventListener('click', this._onFirstClick, { passive: true })
    },

    onFirstTouch (e) {
      this.touch = true
      document.removeEventListener('click', this.leaveOnClickOutside)
      document.removeEventListener('touchstart', this._onFirstTouch)
      this._onTouchOutside = (ev) => { this.leaveOnClickOutside(ev) }
      document.addEventListener('touchstart', this._onTouchOutside, { passive: true })
      this.leaveOnClickOutside(e)
    },

    onFirstClick (e) {
      document.removeEventListener('click', this._onFirstClick)
      if (!this.touch) {
        document.removeEventListener('touchstart', this._onFirstTouch)
        this._onClickOutside = (ev) => { this.leaveOnClickOutside(ev) }
        document.addEventListener('click', this._onClickOutside, { passive: true })
        this.leaveOnClickOutside(e)
      }
    },

    leaveOnClickOutside (event) {
      if (this.forceExpand) return
      const el = this.$refs['text-line']
      if (!el) return
      if (!el.contains(event.target) &&
        el.classList.contains(this.expandedClass)) {
        this.collapse()
      }
    }
  }
}
</script>
<style scoped>
/* * {
  will-change: height;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
} */
</style>
<style lang="scss">
$blocks: p, h1, h2, h3, h4, h5, h6, ol, ul, pre, address, blockquote, dl, div, fieldset, form, hr, noscript, table;

@mixin all-blocks {
  @each $el in $blocks {
    #{$el} {
      @content
    }
  }
}

.expandable-text-line {
  box-sizing: border-box;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: pointer;
  width: 100%;
  max-width: 100vw;
  transition: height 0.2s ease-in-out;

  @include all-blocks {
    display: inline;

    &:not(:first-child)::before {
      content: "\00a0 "
    }

    &:not(:last-child)::after {
      content: "\00a0 "
    }
  }

  br {
    display: none;
  }

  &.__extl-expanded {
    max-height: 100%;
    overflow: auto;
    cursor: auto;
    transition: height 0.2s ease-in-out;

    @include all-blocks {
      display: block;

      &:before {
        content: none
      }

      &:first-child {
        margin-top: 0;
      }
    }

    br {
      display: inline;
    }
  }
}
</style>
